/**
 * MUS-113 - feedback / transition clip advance timing.
 *
 * Both the web and native branches of `ExerciseBase.showPassAnimation` wait
 * before advancing to the next exercise so the on-completion feedback clip is
 * heard in full. The rule is identical on both surfaces: wait the clip's real
 * duration, but never less than a fixed floor so short clips still advance
 * promptly.
 *
 * This lives in one place on purpose. MUS-113 was a divergence bug: the native
 * branch waited the clip's true length while the web branch used a hardcoded
 * 1000ms, so any clip longer than a second had the transition tear the scene
 * down mid-playback and cut the clip off. Sharing one helper makes the two
 * branches identical by construction rather than by coincidence, so the floor
 * or the formula can only ever be changed for both at once.
 */

/** Minimum advance delay, so very short clips still advance promptly. */
export const FEEDBACK_ADVANCE_FLOOR_MS = 500;

/**
 * Delay (ms) before advancing to the next exercise after the feedback clip
 * starts: the clip's real duration, floored at {@link FEEDBACK_ADVANCE_FLOOR_MS}.
 *
 * @param durationSecs clip duration in seconds. A non-finite or non-positive
 *   value (a duration that could not be read) collapses to the floor.
 */
export function feedbackAdvanceDelayMs(durationSecs: number): number {
  const durationMs = Number.isFinite(durationSecs) && durationSecs > 0 ? durationSecs * 1000 : 0;
  return Math.max(durationMs, FEEDBACK_ADVANCE_FLOOR_MS);
}
